import React from 'react';
import styled from 'astroturf/react';

import Badge from 'js/design_system/Badge';

import { BundleWork } from './types';
import { WorkCard } from './WorkCard';

/**
 * V8 (`source_anchor_v8`) replacement for the source-paper card.
 * Wraps the existing `WorkCard` with a "Your paper" chip overlay
 * + 1px primary-color accent border + slight elevation. Drops the
 * "(included)" header text — the chip carries the same role with
 * stronger framing.
 *
 * T8 (anchor on the user's paper) extension: T8's bet (anchoring is
 * real) was previously tested only via the first-bullet copy. The
 * source-paper *card* itself had no real visual treatment — just a
 * `<Body size="xs">` "(included)" header above an otherwise-identical
 * WorkCard. V8 introduces a stronger visual treatment so the anchor
 * lands.
 *
 * Eligibility falls back inside `renderIncludedWork`: when
 * `sourceWorkPreview` is `undefined` (license-blocked source works,
 * or before the API resolves), the resolver returns `null` —
 * matches CONTROL.
 */
export interface SourceAnchoredWorkCardProps {
  work: BundleWork;
  showAnnotations: boolean;
  badgePlacement: 'side' | 'below' | 'above';
}

export function SourceAnchoredWorkCard({
  work,
  showAnnotations,
  badgePlacement,
}: SourceAnchoredWorkCardProps) {
  return (
    <AnchoredWrapper>
      <ChipOverlay>
        <Badge text="Your paper" />
      </ChipOverlay>
      <WorkCard
        work={work}
        showRelevancy={false}
        showAnnotations={showAnnotations}
        badgePlacement={badgePlacement}
      />
    </AnchoredWrapper>
  );
}

const AnchoredWrapper = styled.div`
  position: relative;
  border: 1px solid var(--primary-brand, #0645b1);
  border-radius: var(--border-radius-radius-md, 8px);
  box-shadow: 0 2px 6px rgba(6, 69, 177, 0.15);
  margin-top: var(--spacing-xs-12, 12px);
  margin-bottom: var(--spacing-xs-8, 8px);
`;

const ChipOverlay = styled.div`
  position: absolute;
  top: -14px;
  left: var(--spacing-xs-12, 12px);
  z-index: 1;
  pointer-events: none;
`;
